import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import shortid from "shortid";

import { assertNativeError } from "@examspark/common";
import { validateAcademicCalendar } from "@examspark/common/dist/utils/coursePlanner";
import {
  useMutationCreateAcademicCalendar,
  useAppContext,
} from "@examspark/common-app";
import { toast } from "@examspark/common-ui";
import { useArrayReducer } from "@examspark/common-ui/dist/hooks/useArray";
import {
  ResourceMaturity,
  AcademicCalendar,
  GradingPeriod,
  ResourceVisibilityType,
  Weekday,
} from "@examspark/types";

import { getUrlForObject } from "../../../utils";

import {
  CalendarForm,
  GradingPeriodWithId,
  HolidayWithId,
} from "./WorkspaceCalendarForm";

export const CreateWorkspaceCalendar = () => {
  const name = useState<string>("Untitled Calendar");
  const startAt = useState<string | null>(null);
  const endAt = useState<string | null>(null);
  const instructionWeekdays = useState<Weekday[]>([
    Weekday.Monday,
    Weekday.Tuesday,
    Weekday.Wednesday,
    Weekday.Thursday,
    Weekday.Friday,
  ]);
  const visibility = useState<ResourceVisibilityType | null>(
    ResourceVisibilityType.WorkspaceShareable,
  );

  const holidays = useArrayReducer<HolidayWithId>([]);

  const gradingPeriodLength = useState<string | null>("6_WEEKS");
  const gradingPeriods = useArrayReducer<GradingPeriodWithId>(
    Array(6)
      .fill(null)
      .map((_, idx) => ({
        id: shortid(),
        label: `Grading Period ${idx + 1}`,
      })),
  );

  const [context] = useAppContext();
  const navigate = useNavigate();
  const workspaceId = context.workspaceUser?.workspace?.id;
  const bankId = context.appLicenseConfig?.userContentBankId;

  const mutationSave = useMutationCreateAcademicCalendar();

  const client = useQueryClient();

  const onSave = async () => {
    if (!workspaceId) {
      console.error("no workspace id");
      return;
    }

    if (!bankId) {
      console.error(
        "Missing user content bank id. This calendar will not be sharable.",
      );
    }

    try {
      const sortedNonInstructionDates = holidays.values
        .map((holiday) => ({
          startAt: holiday.startAt,
          endAt: holiday.endAt,
          label: holiday.label,
          type: holiday.type,
        }))
        .sort((a, b) => {
          // our AWSDates are strings, but since they are yyyy-mm-dd, they still
          // sort correctly
          return (
            a.startAt.localeCompare(b.startAt) ||
            (a.label ?? "").localeCompare(b.label ?? "")
          );
        });

      const calendar: Omit<AcademicCalendar, "id" | "sk"> = {
        schemaVersion: "2020-01-01",
        workspaceId,

        ...(bankId ? { bankId } : {}),
        maturity: ResourceMaturity.Operational,
        visibility: visibility[0],

        name: name[0],
        startAt: startAt[0] as string,
        endAt: endAt[0] as string,
        instructionWeekdays: instructionWeekdays[0],
        nonInstructionDates: sortedNonInstructionDates,
        gradingPeriodLength: gradingPeriodLength[0],
        gradingPeriods: gradingPeriods.values
          .map((gradingPeriod) => ({
            startAt: gradingPeriod.startAt,
            endAt: gradingPeriod.endAt,
            label: gradingPeriod.label,
          }))
          .filter(
            (_, idx) => idx < (gradingPeriodLength[0] === "6_WEEKS" ? 6 : 4),
          ) as GradingPeriod[], // assure TS that start/end times are set,,
      };

      validateAcademicCalendar(calendar);
      const res = await mutationSave.mutateAsync(calendar);

      toast.success("Calendar created");

      const url = getUrlForObject(res.data?.createAcademicCalendar);

      setTimeout(() => (url ? navigate(url) : null), 1000);
    } catch (err) {
      assertNativeError(err);
      toast.error(`Error creating calendar. ${err.message}`);
      return;
    }

    client.invalidateQueries(["academic-calendars"]);
  };

  return (
    <CalendarForm
      action="Create"
      name={name}
      startAt={startAt}
      endAt={endAt}
      instructionWeekdays={instructionWeekdays}
      holidays={holidays}
      gradingPeriodLength={gradingPeriodLength}
      gradingPeriods={gradingPeriods}
      visibility={visibility}
      onSave={onSave}
    />
  );
};
